import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d974e7dtfb6ioa7s1us0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && String(serverMsg).includes('spam')) {
          setFormError('Wystąpił błąd. Spróbuj ponownie.');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || 'Wystąpił błąd. Spróbuj ponownie.');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('Wystąpił błąd sieci. Spróbuj ponownie.');
      setFormStatus('error');
    }
  };

  return (
    <footer className="w-full bg-primary-500 text-background-50 rounded-t-[32px] mt-6 mx-6 md:mx-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none flex items-end justify-center pb-8 opacity-[0.04]">
        <span className="text-[12vw] font-heading font-bold whitespace-nowrap select-none">
          KAKOBUYFINDS
        </span>
      </div>

      <div className="relative z-10 px-8 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h3 className="text-2xl font-heading font-semibold text-background-50 mb-6">
              {t('footer.brand')}
            </h3>
            <p className="text-sm text-background-50/70 leading-relaxed max-w-xs">
              Niezależny przewodnik po Kakobuy Spreadsheet. Znajduj najlepsze produkty, porównuj opcje i oszczędzaj czas.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              {t('footer.navTitle')}
            </h4>
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                {t('footer.home')}
              </Link>
              <Link to="/kakobuy-spreadsheet-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                {t('footer.guide')}
              </Link>
              <Link to="/cssbuy-spreadsheet-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                CSSBuy Guide
              </Link>
              <Link to="/superbuy-spreadsheet-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                Superbuy Guide
              </Link>
              <Link to="/oopbuy-spreadsheet-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                Oopbuy Guide
              </Link>
              <Link to="/shopping-safety-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                {t('footer.safety')}
              </Link>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              {t('footer.socialTitle')}
            </h4>
            <div className="flex flex-col gap-3">
              <a href="#" className="flex items-center gap-2 text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-instagram-line"></i> Instagram
              </a>
              <a href="#" className="flex items-center gap-2 text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-youtube-line"></i> YouTube
              </a>
              <a href="#" className="flex items-center gap-2 text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-facebook-line"></i> Facebook
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-2">
              {t('footer.newsletterTitle')}
            </h4>
            <p className="text-xs text-background-50/60 mb-4">
              {t('footer.newsletterDesc')}
            </p>

            {formStatus === 'success' ? (
              <p className="text-sm text-accent-300 font-medium">Dziękujemy za zapisanie się!</p>
            ) : (
              <form data-readdy-form id="newsletter-form" onSubmit={handleSubmit} className="flex gap-0">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('footer.placeholder')}
                  className="flex-1 bg-background-50/10 border border-background-50/20 rounded-l-full px-4 py-2.5 text-sm text-background-50 placeholder:text-background-50/40 outline-none focus:border-background-50/40"
                />
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />
                <button
                  type="submit"
                  className="bg-background-50 text-primary-500 w-10 h-10 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            )}

            {formStatus === 'error' && (
              <p className="text-xs text-red-300 mt-2">{formError}</p>
            )}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-50/50">
            &copy; {new Date().getFullYear()} Kakobuy Finds. {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-background-50/50 hover:text-background-50/80 transition-colors whitespace-nowrap cursor-pointer">
              {t('footer.privacy')}
            </a>
            <a href="#" className="text-xs text-background-50/50 hover:text-background-50/80 transition-colors whitespace-nowrap cursor-pointer">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}